import { execFileSync } from 'node:child_process'

import * as p from '@clack/prompts'
import pc from 'picocolors'

export interface ServicesPromptResult {
  provider: 'vercel' | 'railway' | 'manual'
  url?: string
}

const VERCEL_NEON_URL = 'https://vercel.com/dashboard/integrations/checkout/neon'

/**
 * Prompt the user to choose a database provider. Cloud choices defer to their
 * provider CLI flows; manual collects a connection string inline. File storage
 * and deployment are selected independently later in init.
 */
export async function promptServices(): Promise<ServicesPromptResult> {
  const choice = await p.select({
    message: 'Connect a PostgreSQL Database',
    options: [
      {
        value: 'vercel' as const,
        label: 'Vercel (Neon Postgress)',
        hint: 'Automatically provision a Neon Postgres database'
      },
      {
        value: 'railway' as const,
        label: 'Railway (Postgres)',
        hint: 'Provision Postgres in your Railway project'
      },
      {
        value: 'manual' as const,
        label: 'Manual',
        hint: 'Manually enter connection details'
      }
    ],
    initialValue: 'vercel' as const
  })

  if (p.isCancel(choice)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  if (choice === 'vercel') {
    return { provider: 'vercel' }
  }

  if (choice === 'railway') {
    return { provider: 'railway' }
  }

  const url = await promptConnectionString()
  return { provider: 'manual', url }
}

/** Browser fallback: open the Vercel Neon checkout when the CLI flow can't run. */
export function openBrowserVercelNeon(): void {
  openBrowser(VERCEL_NEON_URL)
  p.log.info(
    `Opening Vercel... Create a Neon Postgres database, then copy the ${pc.cyan('DATABASE_URL')} from the dashboard.`
  )
}

/** Open a provisioned Neon resource so the user can copy its DATABASE_URL. */
export function openBrowserVercelNeonResource(url: string | undefined): void {
  if (!url) {
    openBrowserVercelNeon()
    return
  }

  openBrowser(url)
  p.log.info(
    `Opening Vercel... Copy the Neon ${pc.cyan('DATABASE_URL')} from the database dashboard, then paste it below.`
  )
}

/** Text prompt for the connection string with quote-stripping validation. */
export async function promptConnectionString(): Promise<string> {
  const input = await p.text({
    message: 'Paste your PostgreSQL connection string',
    placeholder: 'postgres://user:pass@host/db',
    validate(val) {
      const normalized = val?.trim() ?? ''

      if (!normalized) {
        return 'A connection string is required to continue'
      }

      const stripped = normalized.replace(/^['"]|['"]$/g, '')
      if (!stripped.startsWith('postgres://') && !stripped.startsWith('postgresql://')) {
        return 'Must start with postgres:// or postgresql://'
      }
    }
  })

  if (p.isCancel(input)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  return (input as string).replace(/^['"]|['"]$/g, '').trim()
}

function openBrowser(url: string): void {
  try {
    const platform = process.platform
    if (platform === 'darwin') {
      execFileSync('open', [url], { stdio: 'ignore' })
    } else if (platform === 'win32') {
      execFileSync('cmd', ['/c', 'start', url], { stdio: 'ignore' })
    } else {
      execFileSync('xdg-open', [url], { stdio: 'ignore' })
    }
  } catch {
    // Browser opening is best effort; the prompt still accepts a pasted URL.
  }
}
